// Phase 8A Step 4 — Agent deployment display (§4.6).
//
// When SIKA invokes tools during a response, this component renders
// a compact deployment status widget showing each step with its
// current state: queued → active → done → failed.
//
// Two variants:
//   - Inline: rendered inside a message bubble (.agent-deploy-inline)
//   - Zone C: rendered in the context pane (.agent-steps)

import { Icon } from "../../components/icons/Icon.tsx";

export type AgentStepStatus = "queued" | "active" | "done" | "failed";

export interface AgentStep {
  id: string;
  label: string;
  status: AgentStepStatus;
  detail?: string;
}

interface Props {
  steps: AgentStep[];
  variant?: "inline" | "panel";
}

const STATUS_ICONS: Record<AgentStepStatus, string> = {
  queued: "rollover",
  active: "bolt",
  done: "check",
  failed: "alert",
};

function StepIcon({ status }: { status: AgentStepStatus }): JSX.Element {
  return (
    <span className={`step-icon ${status}`}>
      <Icon name={STATUS_ICONS[status]} size={12} />
    </span>
  );
}

export function SikaAgentDeploy({ steps, variant = "inline" }: Props): JSX.Element {
  const wrapperClass = variant === "panel" ? "agent-steps" : "agent-deploy-inline";
  const doneCount = steps.filter((s) => s.status === "done").length;

  return (
    <div className={wrapperClass}>
      {variant === "inline" && (
        <div className="deploy-header">
          Deploying {steps.length} agent{steps.length !== 1 ? "s" : ""}… ({doneCount}/{steps.length})
        </div>
      )}

      {variant === "panel" && (
        <div className="deploy-header">
          Agent deployment · {doneCount}/{steps.length}
        </div>
      )}

      {steps.map((step, i) => (
        <div
          key={step.id}
          className={`agent-step ${step.status}`}
          style={{ animationDelay: `${i * 80}ms` }}
        >
          <StepIcon status={step.status} />
          <span className="step-label">{step.label}</span>
          {step.detail && <span className="step-result">{step.detail}</span>}
        </div>
      ))}
    </div>
  );
}

/** Utility: build agent steps from tool_use metadata in a chat message. */
export function parseAgentSteps(toolUse: unknown): AgentStep[] | null {
  if (!toolUse || typeof toolUse !== "object") return null;
  const tu = toolUse as { agent_steps?: unknown[] };
  if (!Array.isArray(tu.agent_steps)) return null;

  return tu.agent_steps.map((s: unknown, i: number) => {
    const step = s as { id?: string; label?: string; status?: string; detail?: string };
    return {
      id: step.id ?? `step-${i}`,
      label: step.label ?? `Step ${i + 1}`,
      status: (step.status as AgentStepStatus) ?? "queued",
      detail: step.detail,
    };
  });
}
